import React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { DocumentsDataTableQuery, DocumentsDocument } from 'generated/resources/datatable.graphql'

import { useDataTable } from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useRemoveFromTrashBinDocument from 'modules/documents/hooks/documents/useRemoveFromTrashBinDocument'
import useRestoreFromTrashBinDocument from 'modules/documents/hooks/documents/useRestoreFromTrashBinDocument'
import { canDeleteDocuments } from 'modules/documents/lib/Documents'

import DeleteForeverModal from './DeleteForeverModal'
import RestoreModal from './RestoreModal'

const BulkActions = () => {
  const { data, selectedRows } = useDataTable<DocumentsDataTableQuery, DocumentsDocument>()
  const selectedIds = useUnsafeMemo(() => {
    return [...selectedRows]
  }, [selectedRows])
  const selectedDocuments = useUnsafeMemo(() => {
    return data.filter(({ id }) => selectedIds.includes(id))
  }, [data, selectedIds])
  const documentName = useUnsafeMemo(() => {
    return selectedDocuments[0]?.filename || ''
  }, [selectedDocuments])
  const [restoreFilesModalOpen, setRestoreFilesModalOpen] = React.useState(false)
  const [deleteFilesForeverModalOpen, setDeleteFilesForeverModalOpen] = React.useState(false)
  const { restoreDocumentsFromTrashBin, loading: loadingRestore } = useRestoreFromTrashBinDocument({
    fromDataTable: true,
  })
  const { removeDocumentsFromTrashBin, loading: loadingRemove } = useRemoveFromTrashBinDocument({
    fromDataTable: true,
  })

  const onSubmitRestore = async () => {
    const result = await restoreDocumentsFromTrashBin({ documentIds: selectedIds })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.trash_bin.restore.success', { count: selectedIds.length }),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
  }

  const onSubmitDeleteForever = async () => {
    const result = await removeDocumentsFromTrashBin({ documentIds: selectedIds })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.trash_bin.delete_forever.success', {
          count: selectedIds.length,
        }),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
  }

  const isLoading = loadingRestore || loadingRemove
  const canDeleteSelectedDocuments = canDeleteDocuments(selectedDocuments)

  if (!selectedDocuments.length || !canDeleteSelectedDocuments) {
    return null
  }

  if (isLoading) {
    return (
      <Button iconName="spinner" type="secondary" loading>
        {i18n.t('common.actions')}
      </Button>
    )
  }

  return (
    <>
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Button.Decorative type="secondary">{i18n.t('common.actions')}</Button.Decorative>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="end">
          <PopoverMenu.TouchableOption
            title={i18n.t('documents.trash_bin.restore_files')}
            icon="history"
            onClick={() => setRestoreFilesModalOpen(true)}
          />
          <PopoverMenu.TouchableOption
            title={i18n.t('documents.trash_bin.delete')}
            icon="delete"
            danger
            onClick={() => setDeleteFilesForeverModalOpen(true)}
          />
        </PopoverMenu.Content>
      </PopoverMenu>
      {restoreFilesModalOpen && (
        <RestoreModal
          filename={documentName}
          totalDocuments={selectedDocuments.length}
          loading={loadingRestore}
          onSubmit={onSubmitRestore}
          onClose={() => setRestoreFilesModalOpen(false)}
        />
      )}
      {deleteFilesForeverModalOpen && (
        <DeleteForeverModal
          filename={documentName}
          totalDocuments={selectedDocuments.length}
          onClose={() => setDeleteFilesForeverModalOpen(false)}
          onSubmit={onSubmitDeleteForever}
          loading={loadingRemove}
        />
      )}
    </>
  )
}

export default BulkActions
